import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBranding } from '@/hooks/useBranding';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Upload, Palette, Image as ImageIcon, Loader2 } from 'lucide-react';
import { AllowedDomainsCard } from './AllowedDomainsCard';

export function BrandingSettings() {
  const { user } = useAuth();
  const { branding, isLoading, updateBranding, uploadBrandingImage, isUpdating } = useBranding();

  const [orgName, setOrgName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#2563eb');
  const [secondaryColor, setSecondaryColor] = useState('#7c3aed');
  const [accentColor, setAccentColor] = useState('#059669');
  const [headerMode, setHeaderMode] = useState<'light' | 'dark'>('light');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);

  // Sync state when branding data loads
  useEffect(() => {
    if (branding) {
      setOrgName(branding.organization_name || '');
      setPrimaryColor(branding.primary_color || '#2563eb');
      setSecondaryColor(branding.secondary_color || '#7c3aed');
      setAccentColor(branding.accent_color || '#059669');
      setHeaderMode(branding.header_mode || 'light');
    }
  }, [branding]);

  if (user?.role !== 'super_admin' && user?.role !== 'manager') {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Palette className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg font-semibold mb-2">Access Restricted</p>
          <p className="text-muted-foreground">Only Super Admins can configure branding</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handleSave = async () => {
    const updates: Record<string, unknown> = {
      organization_name: orgName,
      primary_color: primaryColor,
      secondary_color: secondaryColor,
      accent_color: accentColor,
      header_mode: headerMode,
    };

    // Only overwrite stored image paths when a new file was picked — the values
    // held in state are freshly signed URLs, not the raw storage paths.
    if (logoFile) {
      const logoUrl = await uploadBrandingImage(logoFile, 'logo');
      if (!logoUrl) return;
      updates.logo_url = logoUrl;
    }

    if (bannerFile) {
      const bannerUrl = await uploadBrandingImage(bannerFile, 'banner');
      if (!bannerUrl) return;
      updates.login_banner_url = bannerUrl;
    }

    updateBranding(updates);
    setLogoFile(null);
    setBannerFile(null);
  };


  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold">Branding & White-Label</h2>
        <p className="text-muted-foreground">Customize the appearance of your system</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Organization Details</CardTitle>
          <CardDescription>Set your organization name and logo</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Organization Name</Label>
            <Input
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Enter organization name"
            />
          </div>

          <div>
            <Label>Logo</Label>
            <div className="mt-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-muted/50 w-fit"
              >
                <Upload className="h-4 w-4" />
                <span>{logoFile ? logoFile.name : 'Upload Logo'}</span>
              </label>
            </div>
            {branding?.logo_url && !logoFile && (
              <img src={branding.logo_url} alt="Current logo" className="mt-2 h-16 object-contain" />
            )}
          </div>

          <div>
            <Label>Login Banner Image</Label>
            <div className="mt-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                className="hidden"
                id="banner-upload"
              />
              <label
                htmlFor="banner-upload"
                className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-muted/50 w-fit"
              >
                <ImageIcon className="h-4 w-4" />
                <span>{bannerFile ? bannerFile.name : 'Upload Banner'}</span>
              </label>
            </div>
            {branding?.login_banner_url && !bannerFile && (
              <img src={branding.login_banner_url} alt="Current banner" className="mt-2 h-32 object-cover rounded-lg" />
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Color Theme</CardTitle>
          <CardDescription>Customize your brand colors</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Primary Color</Label>
              <div className="flex gap-2 items-center mt-2">
                <Input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-16 h-10"
                />
                <Input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <Label>Secondary Color</Label>
              <div className="flex gap-2 items-center mt-2">
                <Input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-16 h-10"
                />
                <Input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <Label>Accent Color</Label>
              <div className="flex gap-2 items-center mt-2">
                <Input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-16 h-10"
                />
                <Input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <div>
            <Label>Header Mode</Label>
            <Select value={headerMode} onValueChange={(v: 'light' | 'dark') => setHeaderMode(v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="p-4 border rounded-lg">
            <p className="text-sm font-medium mb-3">Preview</p>
            <div className="flex gap-2">
              <div className="w-20 h-20 rounded-lg" style={{ backgroundColor: primaryColor }} />
              <div className="w-20 h-20 rounded-lg" style={{ backgroundColor: secondaryColor }} />
              <div className="w-20 h-20 rounded-lg" style={{ backgroundColor: accentColor }} />
            </div>
          </div>
        </CardContent>
      </Card>

      <AllowedDomainsCard />

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isUpdating}>
          {isUpdating ? 'Saving...' : 'Save Branding'}
        </Button>
      </div>
    </div>
  );
}
